<template>
  <Editor
    v-if="ready"
    v-model="content"
    license-key="gpl"
    tinymce-script-src="/tinymce/tinymce.min.js"
    :init="editorInit"
  />
</template>

<script lang="ts" setup>
/**
 * Hướng dẫn sử dụng — TinyMCE (self-hosted)
 *
 * Global: <h-tiny v-model="html" />
 * Import: import TinyMce from '@/components/editors/TinyMce.vue'
 *
 * Không import tinymce/plugins/* qua webpack — TinyMCE tự load từ /tinymce
 * (copy bởi vue.config.js CopyPlugin).
 */
import { ref, computed, onMounted } from "vue";
import Editor from "@tinymce/tinymce-vue";
import { uploadFileOne } from "@/api/index";

interface Props {
  modelValue?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const ready = ref(false);

const content = computed({
  get: () => props.modelValue ?? "",
  set: (val: string) => emit("update:modelValue", val),
});

function uploadImageHandler(blobInfo: any): Promise<string> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append("documents", blobInfo.blob());
    formData.append("ocr", "Y");
    formData.append("fileDraft", "N");
    formData.append("documentClass", "TINIMCE");

    uploadFileOne(formData)
      .then((res: any) => {
        const urlImage = res?.data?.data;
        if (!urlImage?.fileUrl) {
          reject("Lỗi khi upload ảnh");
          return;
        }
        const url = urlImage.fileUrl + urlImage.folderPath + urlImage.fileName;
        resolve(url);
      })
      .catch(() => {
        reject("Lỗi kết nối");
      });
  });
}

const editorInit = {
  base_url: "/tinymce",
  suffix: ".min",
  license_key: "gpl",
  height: 300,
  menubar: true,
  branding: false,
  plugins: [
    "lists",
    "link",
    "image",
    "table",
    "autolink",
    "searchreplace",
    "advlist",
    "charmap",
    "code",
    "preview",
    "fullscreen",
    "media",
    "insertdatetime",
    "help",
    "wordcount",
  ],
  toolbar:
    "undo redo | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | forecolor backcolor removeformat | image link media | preview fullscreen",
  toolbar_mode: "wrap",
  automatic_uploads: true,
  images_upload_credentials: true,
  images_file_types: "jpg,jpeg,png,gif,webp,svg",
  images_upload_handler: uploadImageHandler,
};

onMounted(() => {
  ready.value = true;
});
</script>

<style>
.tox-tinymce {
  width: 100%;
  height: 300px;
}
</style>
